/* ==========================================================================
   SURVENTURE — product.js (product pages only, deferred)
   Gallery · zoom · variant picker · quantity bundles · sticky add to cart ·
   complementary products · lazy recommendations · review "show more"
   ========================================================================== */
(() => {
  'use strict';

  if (window.SVProductLoaded) return;
  window.SVProductLoaded = true;

  const SV = window.SV || {};
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ------------------------------------------------------------------------
     <sv-gallery>
     ---------------------------------------------------------------------- */
  class SVGallery extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-gallery-track]', this);
      if (!this.track || this.bound) return;
      this.bound = true;
      this.slides = $$('.sv-gallery__slide', this.track);
      this.thumbs = $$('[data-thumb-index]', this);
      this.counter = $('[data-gallery-current]', this);
      this.prev = $('[data-gallery-prev]', this);
      this.next = $('[data-gallery-next]', this);
      this.dialog = $('[data-zoom-dialog]', this);
      this.index = 0;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.goTo(parseInt(thumb.dataset.thumbIndex, 10)));
      });
      if (this.prev) this.prev.addEventListener('click', () => this.goTo(this.index - 1));
      if (this.next) this.next.addEventListener('click', () => this.goTo(this.index + 1));

      this.track.addEventListener(
        'scroll',
        () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = null;
            const width = this.track.clientWidth || 1;
            this.setActive(Math.round(this.track.scrollLeft / width));
          });
        },
        { passive: true }
      );

      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track) return;
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.index + 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.index - 1);
        }
      });

      if (this.dialog && this.dataset.zoom !== 'false') {
        this.addEventListener('click', (event) => {
          const trigger = event.target.closest('[data-zoom-index]');
          if (!trigger) return;
          this.openZoom(parseInt(trigger.dataset.zoomIndex, 10), trigger);
        });
      }

      const initial = this.slides.findIndex((slide) => slide.hasAttribute('data-initial'));
      if (initial > 0) this.goTo(initial, true);
      else this.setActive(0);
    }

    goTo(index, instant) {
      if (!this.slides.length) return;
      const target = Math.max(0, Math.min(this.slides.length - 1, index));
      this.track.scrollTo({
        left: this.slides[target].offsetLeft,
        behavior: instant || reduceMotion.matches ? 'auto' : 'smooth',
      });
      this.setActive(target);
    }

    goToMedia(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index > -1) this.goTo(index);
    }

    setActive(index) {
      if (index === this.index && this.initialised) return;
      this.initialised = true;
      this.index = index;
      this.thumbs.forEach((thumb, i) => {
        const active = i === index;
        thumb.classList.toggle('is-active', active);
        if (active) {
          thumb.setAttribute('aria-current', 'true');
          const rail = thumb.closest('.sv-gallery__thumbs');
          if (rail && rail.scrollWidth > rail.clientWidth) {
            rail.scrollTo({ left: thumb.parentElement.offsetLeft - rail.clientWidth / 2 + thumb.clientWidth / 2, behavior: 'smooth' });
          }
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
      if (this.counter) this.counter.textContent = String(index + 1);
      if (this.prev) this.prev.disabled = index <= 0;
      if (this.next) this.next.disabled = index >= this.slides.length - 1;
      this.slides.forEach((slide, i) => {
        if (i === index) return;
        $$('video', slide).forEach((video) => video.pause());
      });
    }

    openZoom(index, opener) {
      if (!this.dialog || !SV.openDialog) return;
      SV.openDialog(this.dialog, opener);
      requestAnimationFrame(() => {
        const item = $(`[data-zoom-item="${index}"]`, this.dialog);
        if (item) item.scrollIntoView({ block: 'start' });
      });
    }
  }
  customElements.define('sv-gallery', SVGallery);

  /* ------------------------------------------------------------------------
     <sv-product-info> — variant selection, price, availability
     ---------------------------------------------------------------------- */
  class SVProductInfo extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      const json = $('[data-variants-json]', this);
      try {
        this.variants = json ? JSON.parse(json.textContent) : [];
      } catch (error) {
        this.variants = [];
      }
      this.form = document.getElementById(this.dataset.formId);
      this.picker = $('sv-variant-picker', this);
      this.section = this.closest('[data-section]');
      this.gallery = this.section && $('sv-gallery', this.section);
      this.sticky = document.querySelector(`sv-sticky-atc[data-form-id="${this.dataset.formId}"]`);

      const idInput = this.form && $('[data-variant-input]', this.form);
      const currentId = idInput ? parseInt(idInput.value, 10) : null;
      this.current = this.variants.find((variant) => variant.id === currentId) || this.variants[0];

      if (this.picker) {
        this.picker.addEventListener('change', () => this.onOptionChange());
      }
      if (this.form) {
        this.form.addEventListener('sv:form:reset', () => this.renderButtons(this.current));
      }
    }

    selectedOptions() {
      return $$('fieldset', this.picker).map((fieldset) => {
        const checked = $('input:checked', fieldset);
        return checked ? checked.value : null;
      });
    }

    onOptionChange() {
      const options = this.selectedOptions();
      $$('fieldset', this.picker).forEach((fieldset, index) => {
        const label = $('[data-option-value]', fieldset);
        if (label) label.textContent = options[index] || '';
      });
      this.updateAvailability(options);

      const variant = this.variants.find((candidate) => candidate.options.every((value, index) => value === options[index]));
      this.current = variant || null;

      if (!variant) {
        this.renderButtons(null);
        return;
      }

      const idInput = this.form && $('[data-variant-input]', this.form);
      if (idInput) {
        idInput.value = String(variant.id);
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url.toString());

      this.renderPrice(variant);
      this.renderButtons(variant);
      this.renderStock(variant);
      if (variant.mediaId && this.gallery && typeof this.gallery.goToMedia === 'function') {
        this.gallery.goToMedia(variant.mediaId);
      }
      this.dispatchEvent(new CustomEvent('sv:variant:change', { bubbles: true, detail: { variant } }));
    }

    updateAvailability(options) {
      $$('fieldset', this.picker).forEach((fieldset, position) => {
        $$('input', fieldset).forEach((input) => {
          const test = options.slice();
          test[position] = input.value;
          const available = this.variants.some(
            (variant) => variant.available && variant.options.every((value, index) => test[index] === null || value === test[index])
          );
          const label = input.closest('label');
          if (label) label.classList.toggle('is-unavailable', !available);
        });
      });
    }

    renderPrice(variant) {
      const current = $('[data-price-current]', this);
      const compare = $('[data-price-compare]', this);
      const badge = $('[data-price-badge]', this);
      if (current) current.textContent = variant.price;
      if (compare) {
        compare.hidden = !variant.compare;
        compare.textContent = variant.compare || '';
      }
      if (badge) {
        badge.hidden = !variant.save;
        if (variant.save) badge.textContent = (this.dataset.saveTemplate || '[percent] %').replace('[percent]', variant.save);
      }
      const stickyPrice = this.sticky && $('[data-sticky-price]', this.sticky);
      if (stickyPrice) stickyPrice.textContent = variant.price;
    }

    renderButtons(variant) {
      const strings = SV.strings || {};
      const available = Boolean(variant && variant.available);
      const label = !variant ? strings.unavailable : available ? strings.addToCart : strings.soldOut;
      const atc = this.form && $('[data-atc]', this.form);
      if (atc) {
        atc.disabled = !available;
        const text = $('[data-atc-label]', atc);
        if (text && label) text.textContent = label;
      }
      if (this.sticky) {
        const button = $('[data-sticky-add]', this.sticky);
        if (button) button.disabled = !available;
        const text = $('[data-sticky-label]', this.sticky);
        if (text && label) text.textContent = label;
      }
    }

    renderStock(variant) {
      const stock = $('[data-stock]', this);
      if (!stock) return;
      const dot = $('[data-stock-dot]', stock);
      const label = $('[data-stock-label]', stock);
      const line = $('[data-stock-line]', stock);
      const urgency = $('[data-urgency]', stock);
      const strings = SV.strings || {};
      const threshold = Number(stock.dataset.threshold || 0);
      const qty = variant.inventory;
      const low = variant.available && qty != null && qty > 0 && qty <= threshold;
      if (dot) dot.classList.toggle('is-out', !variant.available);
      if (label) {
        if (!label.dataset.inStock) label.dataset.inStock = label.textContent.trim();
        label.textContent = variant.available ? label.dataset.inStock : strings.soldOut || label.textContent;
      }
      if (urgency) {
        urgency.hidden = !low;
        if (low) {
          const words = qty === 1 ? 'قطعة واحدة' : qty === 2 ? 'قطعتان' : qty <= 10 ? qty + ' قطع' : qty + ' قطعة';
          const q = $('[data-urgency-qty]', urgency);
          const bar = $('[data-urgency-bar]', urgency);
          if (q) q.textContent = words;
          if (bar) bar.style.width = Math.max(8, Math.min(100, qty * 4)) + '%';
        }
      }
      if (line) line.hidden = low;
    }
  }
  customElements.define('sv-product-info', SVProductInfo);

  customElements.define('sv-variant-picker', class extends HTMLElement {});

  /* ------------------------------------------------------------------------
     <sv-bundle-tiers> — 1× / 2× / 3× quantity options. The radios are named
     "quantity" and belong to the product form, so they submit on their own;
     this keeps the selected card, the total and the sticky bar in sync.
     ---------------------------------------------------------------------- */
  class SVBundleTiers extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.form = document.getElementById(this.dataset.formId);
      this.sum = this.form && $('[data-bundle-sum]', this.form);
      this.sticky = document.querySelector(`sv-sticky-atc[data-form-id="${this.dataset.formId}"]`);
      const json = $('[data-tier-prices]', this);
      try {
        this.prices = json ? JSON.parse(json.textContent) : null;
      } catch (error) {
        this.prices = null;
      }

      this.addEventListener('change', (event) => {
        if (event.target.matches('input[type="radio"]')) this.sync();
      });
      const info = this.closest('sv-product-info');
      if (info) info.addEventListener('sv:variant:change', (event) => this.reprice(event.detail.variant));
      // The browser may restore a different checked radio on reload.
      this.sync();
    }

    sync() {
      const radio = $('input[type="radio"]:checked', this);
      $$('.sv-bundle', this).forEach((option) => option.classList.toggle('is-selected', Boolean(radio) && option.contains(radio)));
      const total = radio && $('[data-tier-total]', radio.closest('.sv-bundle'));
      if (!total) return;
      if (this.sum) this.sum.textContent = total.textContent;
      const stickyPrice = this.sticky && $('[data-sticky-price]', this.sticky);
      if (stickyPrice) {
        const qty = parseInt(radio.value, 10) || 1;
        stickyPrice.textContent = qty > 1 ? `${qty}× · ${total.textContent}` : total.textContent;
      }
    }

    reprice(variant) {
      const data = variant && this.prices && this.prices[variant.id];
      if (data) {
        $$('.sv-bundle', this).forEach((option, index) => {
          const tier = data.tiers[index];
          if (!tier) return;
          [
            ['[data-tier-total]', tier.total],
            ['[data-tier-compare]', tier.compare],
            ['[data-tier-unit]', tier.unit],
          ].forEach(([selector, text]) => {
            const element = $(selector, option);
            if (element) element.textContent = text;
          });
        });
        if (data.image) {
          // Only product images follow the variant; uploaded option images stay.
          $$('[data-variant-image] img', this).forEach((img) => {
            img.removeAttribute('srcset');
            img.src = data.image;
          });
        }
      }
      this.sync();
    }
  }
  customElements.define('sv-bundle-tiers', SVBundleTiers);

  /* ------------------------------------------------------------------------
     <sv-sticky-atc>
     ---------------------------------------------------------------------- */
  class SVStickyATC extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.form = document.getElementById(this.dataset.formId);
      this.atc = this.form && $('[data-atc]', this.form);
      this.button = $('[data-sticky-add]', this);
      if (!this.atc || !this.button || !('IntersectionObserver' in window)) return;
      this.bound = true;

      this.button.addEventListener('click', () => {
        if (this.atc.disabled) {
          this.form.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
          return;
        }
        if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(this.atc);
        else this.atc.click();
      });

      this.form.addEventListener('submit', () => this.button.classList.add('is-loading'));
      this.form.addEventListener('sv:form:reset', () => this.button.classList.remove('is-loading'));
      document.addEventListener('sv:cart:added', () => this.button.classList.remove('is-loading'));

      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.toggle(scrolledPast);
        },
        { threshold: 0 }
      );
      this.observer.observe(this.atc);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    toggle(show) {
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', String(!show));
      this.button.tabIndex = show ? 0 : -1;
    }
  }
  customElements.define('sv-sticky-atc', SVStickyATC);

  /* ------------------------------------------------------------------------
     <sv-complementary> — Search & Discovery complementary products
     ---------------------------------------------------------------------- */
  class SVComplementary extends HTMLElement {
    connectedCallback() {
      if (this.bound || this.hasAttribute('data-manual')) return;
      this.bound = true;
      const list = $('[data-comp-list]', this);
      if (!list || list.children.length || !this.dataset.url) return;
      const load = async () => {
        try {
          const response = await fetch(this.dataset.url);
          if (!response.ok) return;
          const results = $('[data-upsell-results]', parseHTML(await response.text()));
          if (results) list.innerHTML = results.innerHTML;
        } catch (error) {
          /* optional */
        }
      };
      // The block itself stays hidden until it has products, so observe a visible ancestor.
      const target = this.closest('.sv-product__info') || this.parentElement;
      if ('IntersectionObserver' in window && target) {
        const observer = new IntersectionObserver(
          (entries) => {
            if (!entries[0].isIntersecting) return;
            observer.disconnect();
            load();
          },
          { rootMargin: '400px 0px' }
        );
        observer.observe(target);
      } else {
        load();
      }
    }
  }
  customElements.define('sv-complementary', SVComplementary);

  /* ------------------------------------------------------------------------
     <sv-recommendations> — lazy product recommendations section
     ---------------------------------------------------------------------- */
  class SVRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.bound || !this.dataset.url) return;
      this.bound = true;
      const load = async () => {
        try {
          const response = await fetch(this.dataset.url);
          if (!response.ok) return;
          const doc = parseHTML(await response.text());
          const fresh = $('sv-recommendations', doc);
          if (fresh && fresh.innerHTML.trim()) {
            this.innerHTML = fresh.innerHTML;
            if (SV.initReveal) SV.initReveal(this);
          }
          if (!$('.sv-card', this)) {
            const section = this.closest('.shopify-section');
            if (section && !document.documentElement.classList.contains('sv-design-mode')) section.hidden = true;
          }
        } catch (error) {
          /* optional */
        }
      };
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            if (!entries[0].isIntersecting) return;
            observer.disconnect();
            load();
          },
          { rootMargin: '600px 0px' }
        );
        observer.observe(this);
      } else {
        load();
      }
    }
  }
  customElements.define('sv-recommendations', SVRecommendations);

  /* ------------------------------------------------------------------------
     <sv-show-more> — reveal additional reviews
     ---------------------------------------------------------------------- */
  class SVShowMore extends HTMLElement {
    connectedCallback() {
      const button = $('[data-show-more]', this);
      if (!button || this.bound) return;
      this.bound = true;
      button.addEventListener('click', () => {
        const hidden = $$('[data-more-item][hidden]', this);
        const step = parseInt(this.dataset.step, 10) || 4;
        hidden.slice(0, step).forEach((item, index) => {
          item.hidden = false;
          if (index === 0) {
            const focusable = item.querySelector('figure') || item;
            focusable.setAttribute('tabindex', '-1');
            focusable.focus({ preventScroll: true });
          }
        });
        if ($$('[data-more-item][hidden]', this).length === 0) button.hidden = true;
      });
    }
  }
  customElements.define('sv-show-more', SVShowMore);
})();

  /* ------------------------------------------------------------------------
     Delivery dates: order today → ships after N days → arrives in a range.
     ---------------------------------------------------------------------- */
  (function () {
    const fmt = new Intl.DateTimeFormat('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long' });
    const day = new Intl.DateTimeFormat('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric' });
    const month = new Intl.DateTimeFormat('ar-SA-u-ca-gregory-nu-latn', { month: 'long' });
    const add = (base, n, skipFri) => {
      const d = new Date(base);
      let left = n;
      while (left > 0) {
        d.setDate(d.getDate() + 1);
        if (skipFri && d.getDay() === 5) continue;
        left -= 1;
      }
      return d;
    };
    const fill = () => {
      document.querySelectorAll('[data-eta]').forEach((el) => {
        const skip = el.dataset.skipFriday === 'true';
        const now = new Date();
        const ship = add(now, Number(el.dataset.ship || 1), skip);
        const a = add(now, Number(el.dataset.min || 3), skip);
        const b = add(now, Math.max(Number(el.dataset.max || 6), Number(el.dataset.min || 3)), skip);
        const range = a.getMonth() === b.getMonth()
          ? day.format(a) + ' – ' + day.format(b) + ' ' + month.format(b)
          : fmt.format(a) + ' – ' + fmt.format(b);
        const set = (sel, txt) => { const n = el.querySelector(sel); if (n) n.textContent = txt; };
        set('[data-eta-d0]', fmt.format(now));
        set('[data-eta-d1]', fmt.format(ship));
        set('[data-eta-d2]', range);
      });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fill);
    else fill();
    document.addEventListener('shopify:section:load', fill);
  })();
